/**
 * Block 8.3 (2026-05-26): ФОП Reports page.
 *
 * 3 tabs: Підсумок / Декларації / Контроль ліміту.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  fopReports,
  type AnnualLimitCheck,
  type FopSelfPaymentsSummary,
  type SingleTaxSummary,
} from '@/api/fop_reports';
import LimitCheckBadge from '@/shared/components/LimitCheckBadge';
import { useDefaults } from '@/store/defaults';


type Tab = 'summary' | 'declarations' | 'limit';


const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function FopReportsPage() {
  const [tab, setTab] = useState<Tab>('summary');
  // 2026-07-26: старт з організації за замовчуванням з шапки (було «оберіть»).
  const [orgId, setOrgId] = useState(() => useDefaults.getState().defaultOrgId);
  const [year, setYear] = useState<number>(new Date().getFullYear());

  const orgList = useQuery({
    queryKey: ['orgs-fop'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  const fopOrgs = useMemo(
    () => (orgList.data?.items ?? []).filter((o) => o.is_fop),
    [orgList.data],
  );

  const singleTaxQ = useQuery({
    queryKey: ['fop-summary', orgId, year],
    queryFn: () => fopReports.singleTaxSummary(orgId, year),
    enabled: !!orgId,
  });
  const selfQ = useQuery({
    queryKey: ['fop-self-summary', orgId, year],
    queryFn: () => fopReports.selfPaymentsSummary(orgId, year),
    enabled: !!orgId,
  });
  const limitQ = useQuery({
    queryKey: ['fop-limit', orgId, year],
    queryFn: () => fopReports.annualLimitCheck(orgId, year),
    enabled: !!orgId,
  });

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>ФОП Звіти</h2>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '8px 0', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          <option value="">— Оберіть ФОП-юрособу —</option>
          {fopOrgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} ({o.tax_system})
            </option>
          ))}
        </select>
        <input
          type="number" value={year} min={2020} max={2100}
          style={{ width: 100 }}
          onChange={(e) => setYear(Number(e.target.value))}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #ccc', paddingBottom: 8 }}>
        <button
          onClick={() => setTab('summary')}
          style={{ fontWeight: tab === 'summary' ? 'bold' : 'normal' }}
        >
          Підсумок
        </button>
        <button
          onClick={() => setTab('declarations')}
          style={{ fontWeight: tab === 'declarations' ? 'bold' : 'normal' }}
        >
          Декларації
        </button>
        <button
          onClick={() => setTab('limit')}
          style={{ fontWeight: tab === 'limit' ? 'bold' : 'normal' }}
        >
          Контроль ліміту
        </button>
      </div>

      {!orgId && (
        <div style={{ padding: 24, color: '#666', textAlign: 'center' }}>
          Оберіть ФОП-юрособу для перегляду звітів
        </div>
      )}

      {orgId && tab === 'summary' && (
        <SummaryTab
          singleTax={singleTaxQ.data}
          self={selfQ.data}
        />
      )}

      {orgId && tab === 'declarations' && (
        <DeclarationsTab orgId={orgId} year={year} singleTax={singleTaxQ.data} />
      )}

      {orgId && tab === 'limit' && (
        <LimitCheckTab data={limitQ.data} />
      )}
    </>
  );
}


function SummaryTab({
  singleTax, self,
}: {
  singleTax: SingleTaxSummary | undefined;
  self: FopSelfPaymentsSummary | undefined;
}) {
  if (!singleTax || !self) return <div>Завантаження…</div>;

  return (
    <>
      <h3>Єдиний податок</h3>
      <p>Група {singleTax.tax_group} • Документів проведено: {singleTax.documents_count}</p>
      <p>Сплачено за рік: <strong>{fmt(singleTax.total_tax_amount)} грн</strong></p>

      <h3>ФОП self платежі</h3>
      <p>ЄСВ: <strong>{fmt(self.total_esv_amount)} грн</strong> ({self.esv_documents_count} платежів)</p>
      <p>ВЗ: <strong>{fmt(self.total_wz_amount)} грн</strong> ({self.wz_documents_count} платежів)</p>
    </>
  );
}


function DeclarationsTab({
  orgId, year, singleTax,
}: {
  orgId: string;
  year: number;
  singleTax: SingleTaxSummary | undefined;
}) {
  if (!singleTax) return <div>Завантаження…</div>;
  const group = singleTax.tax_group;

  return (
    <>
      <h3>Друк декларацій</h3>
      {(group === 1 || group === 2) && (
        <p>
          <a
            href={fopReports.declarationGroup1_2Url(orgId, year)}
            target="_blank" rel="noreferrer"
          >
            Річна декларація (Group {group}) {year}
          </a>
        </p>
      )}
      {group === 3 && (
        <div>
          <p>Квартальні декларації:</p>
          <ul>
            {[1, 2, 3, 4].map((q) => (
              <li key={q}>
                <a
                  href={fopReports.declarationGroup3Url(orgId, year, q)}
                  target="_blank" rel="noreferrer"
                >
                  Q{q} {year}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p>
        <a
          href={fopReports.esvAnnualReportUrl(orgId, year)}
          target="_blank" rel="noreferrer"
        >
          Річний звіт ЄСВ + ВЗ {year}
        </a>
      </p>
    </>
  );
}


function LimitCheckTab({ data }: { data: AnnualLimitCheck | undefined }) {
  if (!data) return <div>Завантаження…</div>;
  // Block 10 (2026-05-27): reuse shared LimitCheckBadge mode='full'.
  return <LimitCheckBadge data={data} mode="full" />;
}
